import React, { forwardRef, useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Icon from "./Icon";
import IconButton from "./IconButton";
import Button from "./Button";
import useBreakpoint from "../../hooks/useBreakpoint";
import usePosition from "./usePosition";
import { toDateOnly } from "../../core/dateOnly";
import { pad2, MONTHS_SHORT, MONTHS_LONG, DOW, daysIn, isoToParts, partsToIso, parseDisplay, formatDisplay, cmp, clampIso, todayIso } from "./dateFormat";
import "./DatePicker.scss";

// Fontaine M21 — DatePicker. Values are date-only ISO strings ("YYYY-MM-DD", never Date objects, never
// timezone shifts). Display is ALWAYS day-month-year (DD-MM-YYYY), typed or picked.
// Phone: the V2 drag rollers in a bottom sheet. Laptop: a calendar month grid in a popover.
// Range variant: { start, end } with the same rules.

// ── Roller (from Style Guide V2) — one vertical drum: ▲ / ▼, wheel, arrow keys, drag ──
const DRAG_STEP_PX = 22;
function Roller({ label, display, onStep, ariaValue }) {
  const drag = useRef(null);
  const onPointerDown = (e) => { drag.current = { y: e.clientY, acc: 0 }; e.currentTarget.setPointerCapture?.(e.pointerId); };
  const onPointerMove = (e) => {
    const d = drag.current;
    if (!d) return;
    d.acc += e.clientY - d.y;
    d.y = e.clientY;
    while (d.acc <= -DRAG_STEP_PX) { onStep(1); d.acc += DRAG_STEP_PX; }
    while (d.acc >= DRAG_STEP_PX) { onStep(-1); d.acc -= DRAG_STEP_PX; }
  };
  const endDrag = (e) => { if (drag.current) { e.currentTarget.releasePointerCapture?.(e.pointerId); drag.current = null; } };
  return (
    <div className="ft-roller" role="group" aria-label={label}>
      <span className="ft-roller__label">{label}</span>
      <button type="button" className="ft-roller__btn" aria-label={`${label} up`} onClick={() => onStep(1)}><Icon name="ChevronUp" size="xs" /></button>
      <div
        className="ft-roller__window"
        tabIndex={0}
        role="spinbutton"
        aria-label={label}
        aria-valuetext={ariaValue}
        onWheel={(e) => onStep(e.deltaY < 0 ? 1 : -1)}
        onKeyDown={(e) => { if (e.key === "ArrowUp") { e.preventDefault(); onStep(1); } if (e.key === "ArrowDown") { e.preventDefault(); onStep(-1); } }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onPointerLeave={endDrag}
      >
        <span className="ft-roller__ghost">{display.prev}</span>
        <span className="ft-roller__current">{display.cur}</span>
        <span className="ft-roller__ghost">{display.next}</span>
      </div>
      <button type="button" className="ft-roller__btn" aria-label={`${label} down`} onClick={() => onStep(-1)}><Icon name="ChevronDown" size="xs" /></button>
    </div>
  );
}

function Rollers({ iso, onChange, onDone, min, max }) {
  const start = isoToParts(iso) || isoToParts(todayIso());
  const [p, setP] = useState(start);
  const dim = daysIn(p.m, p.y);
  const set = (next) => { setP(next); onChange(clampIso(partsToIso(next), min, max)); };
  const stepDay = (d) => set({ ...p, d: ((p.d - 1 + d + dim) % dim) + 1 });
  const stepMonth = (d) => { const m = (p.m + d + 12) % 12; set({ ...p, m, d: Math.min(p.d, daysIn(m, p.y)) }); };
  const stepYear = (d) => { const y = p.y + d; set({ ...p, y, d: Math.min(p.d, daysIn(p.m, y)) }); };
  return (
    <div className="ft-rollers">
      <Roller label="Day" display={{ prev: pad2(((p.d - 2 + dim) % dim) + 1), cur: pad2(p.d), next: pad2((p.d % dim) + 1) }} onStep={stepDay} ariaValue={pad2(p.d)} />
      <Roller label="Month" display={{ prev: MONTHS_SHORT[(p.m + 11) % 12], cur: MONTHS_SHORT[p.m], next: MONTHS_SHORT[(p.m + 1) % 12] }} onStep={stepMonth} ariaValue={MONTHS_SHORT[p.m]} />
      <Roller label="Year" display={{ prev: p.y - 1, cur: p.y, next: p.y + 1 }} onStep={stepYear} ariaValue={String(p.y)} />
      <Button className="ft-rollers__done" size="md" onClick={onDone}>Done</Button>
    </div>
  );
}

// ── Calendar month grid (laptop) ──
function Calendar({ value, rangeStart, rangeEnd, onPick, min, max, hoverEnd, onHover }) {
  const anchor = isoToParts(value || rangeStart || todayIso());
  const [view, setView] = useState({ y: anchor.y, m: anchor.m });
  const today = todayIso();
  const cells = useMemo(() => {
    const first = new Date(view.y, view.m, 1);
    const lead = (first.getDay() + 6) % 7; // Monday first
    const dim = daysIn(view.m, view.y);
    const out = [];
    for (let i = 0; i < lead; i++) out.push(null);
    for (let d = 1; d <= dim; d++) out.push(partsToIso({ y: view.y, m: view.m, d }));
    while (out.length % 7) out.push(null);
    return out;
  }, [view]);
  const move = (d) => setView((v) => { const m = v.m + d; return { y: v.y + Math.floor(m / 12), m: ((m % 12) + 12) % 12 }; });
  const effEnd = rangeEnd || hoverEnd;
  return (
    <div className="ft-cal" role="group" aria-label={`${MONTHS_LONG[view.m]} ${view.y}`}>
      <div className="ft-cal__head">
        <IconButton label="Previous month" size="sm" variant="ghost" onClick={() => move(-1)}><Icon name="ChevronLeft" size="sm" /></IconButton>
        <span className="ft-cal__title" aria-live="polite">{MONTHS_LONG[view.m]} {view.y}</span>
        <IconButton label="Next month" size="sm" variant="ghost" onClick={() => move(1)}><Icon name="ChevronRight" size="sm" /></IconButton>
      </div>
      <div className="ft-cal__dow" aria-hidden="true">{DOW.map((d) => <span key={d}>{d}</span>)}</div>
      <div className="ft-cal__grid" role="grid">
        {cells.map((iso, i) => {
          if (!iso) return <span key={`e${i}`} className="ft-cal__cell is-empty" />;
          const disabled = (min && iso < min) || (max && iso > max);
          const selected = iso === value || iso === rangeStart || iso === rangeEnd;
          const inRange = rangeStart && effEnd && cmp(iso, rangeStart) > 0 && cmp(iso, effEnd) < 0;
          return (
            <button
              key={iso}
              type="button"
              role="gridcell"
              className={`ft-cal__cell${selected ? " is-selected" : ""}${inRange ? " is-in-range" : ""}${iso === today ? " is-today" : ""}`}
              aria-selected={selected || undefined}
              aria-label={formatDisplay(iso)}
              disabled={disabled}
              onClick={() => onPick(iso)}
              onMouseEnter={() => onHover?.(iso)}
            >
              {+iso.slice(8, 10)}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * @param {string|null|{start: string|null, end: string|null}} value   ISO date-only; object when `range`
 * @param {(value) => void} onChange
 * @param {boolean} [range]
 * @param {string} [min] / [max]                ISO bounds
 * @param {"auto"|"rollers"|"calendar"} [mode="auto"]   auto = rollers on phone, calendar on laptop
 * @param {string} label                        accessible name
 */
const DatePicker = forwardRef(function DatePicker(
  { value, onChange, range = false, min, max, mode = "auto", label, "aria-label": ariaLabel, placeholder = "DD-MM-YYYY", disabled = false, invalid = false, size = "lg", fullWidth = false, clearable = true, className = "", id, ...rest },
  ref
) {
  const autoId = useId();
  const baseId = id || `ft-date-${autoId}`;
  const { isPhone } = useBreakpoint();
  const useRollers = mode === "rollers" || (mode === "auto" && isPhone);
  const [open, setOpen] = useState(false);
  const [hoverEnd, setHoverEnd] = useState(null);
  const rootRef = useRef(null);
  const panelRef = useRef(null);
  const inputRef = useRef(null);
  const setRefs = (el) => { inputRef.current = el; if (typeof ref === "function") ref(el); else if (ref) ref.current = el; };

  const single = !range ? toDateOnly(value) || null : null;
  const start = range ? toDateOnly(value?.start) || null : null;
  const end = range ? toDateOnly(value?.end) || null : null;
  const display = range ? [start, end].filter(Boolean).map(formatDisplay).join(" → ") : formatDisplay(single);
  const [text, setText] = useState(display);
  useEffect(() => { setText(display); }, [display]);

  const { style } = usePosition(rootRef, panelRef, { open: open && !useRollers, side: "bottom", align: "start" });
  const close = () => { setOpen(false); setHoverEnd(null); };

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (rootRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return; close(); };
    const onKey = (e) => { if (e.key === "Escape") close(); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [open]);

  const commitTyped = () => {
    if (range) return; // typing ranges is not supported — pick them
    if (text.trim() === "") { if (single) onChange?.(null); return; }
    const iso = parseDisplay(text);
    if (iso) onChange?.(clampIso(iso, min, max));
    else setText(display); // revert what we cannot read
  };

  const pick = (iso) => {
    if (!range) { onChange?.(iso); close(); return; }
    if (!start || (start && end)) { onChange?.({ start: iso, end: null }); return; }
    if (iso < start) { onChange?.({ start: iso, end: start }); } else { onChange?.({ start, end: iso }); }
    close();
  };

  const name = ariaLabel || label;
  const classes = ["ft-date", `ft-date--${size === "md" ? "md" : "lg"}`, fullWidth ? "ft-date--full" : "", invalid ? "is-invalid" : "", disabled ? "is-disabled" : "", open ? "is-open" : "", className].filter(Boolean).join(" ");
  const hasValue = range ? !!start : !!single;

  const panel = useRollers ? (
    <Rollers iso={single || start} min={min} max={max} onChange={(iso) => (range ? onChange?.({ start: iso, end }) : onChange?.(iso))} onDone={close} />
  ) : (
    <Calendar value={single} rangeStart={start} rangeEnd={end} hoverEnd={hoverEnd} onHover={range && start && !end ? setHoverEnd : undefined} onPick={pick} min={min} max={max} />
  );

  return (
    <div className={classes} ref={rootRef} {...rest}>
      <div className="ft-date__field">
        <input
          ref={setRefs}
          id={baseId}
          className="ft-date__input"
          value={text}
          placeholder={range ? "DD-MM-YYYY → DD-MM-YYYY" : placeholder}
          inputMode="numeric"
          readOnly={range}
          disabled={disabled}
          aria-label={name}
          aria-invalid={invalid || undefined}
          aria-haspopup="dialog"
          aria-expanded={open}
          onChange={(e) => setText(e.target.value)}
          onBlur={commitTyped}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commitTyped(); } if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); } }}
          onClick={() => range && !disabled && setOpen(true)}
        />
        {clearable && hasValue && !disabled ? (
          <button type="button" className="ft-date__clear" aria-label="Clear date" onClick={() => onChange?.(range ? { start: null, end: null } : null)}><Icon name="X" size="xs" /></button>
        ) : null}
        <button type="button" className="ft-date__btn" aria-label={useRollers ? "Open date rollers" : "Open calendar"} aria-expanded={open} disabled={disabled} onClick={() => setOpen((o) => !o)}>
          <Icon name="Calendar" size="sm" />
        </button>
      </div>

      {open && typeof document !== "undefined"
        ? createPortal(
            useRollers ? (
              <div className="ft-date__scrim" onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}>
                <div className="ft-date__sheet" ref={panelRef} role="dialog" aria-modal="true" aria-label="Choose date">
                  <div className="ft-date__sheet-head"><span>{name || "Choose date"}</span><button type="button" className="ft-date__clear" aria-label="Close" onClick={close}><Icon name="X" size="sm" /></button></div>
                  {panel}
                </div>
              </div>
            ) : (
              // eslint-disable-next-line no-restricted-syntax -- TODO(M02): runtime floating position
              <div className="ft-date__panel" ref={panelRef} role="dialog" aria-label="Choose date" style={style}>
                {panel}
              </div>
            ),
            document.body
          )
        : null}
    </div>
  );
});

export default DatePicker;
